import React, {useState, useEffect, useMemo} from 'react';

import { Link } from 'react-router-dom';
import { useTranslation, withTranslation } from 'react-i18next';

import './ProductsList.css';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/opacity.css';

import {Loader} from "../Loader";
import {Alert, Box, Collapse, IconButton, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BrokenImage from "../../media/images/BrokenImage.png";
import SelectQuantity from "../SelectQuantity";
import PurchasedBadge from "../utils/PurchasedBadge";
import {useCabanaContext} from "../CabanaContext";
import MessageDrawer from "../MessageDrawer";
import ConfirmDeleteItemModal from "../utils/ConfirmDeleteItem";
import QuantityAdjuster from "../QuantityAdjuster";

import {countriesCurrencies} from "../../services/CheckUtils";

interface ProductGridProps {
    type: string | undefined
    products: any;
    wishlistProducts: any;
}

function ProductsList(props: any) {
    const {
        user,
        userType,
        listCode,
        wishlist,
        addProductToWishlist,
        alertOpen,
        contextSetAlertOpen,
        alertText,
        alertType,
        removeProductToWishlist
    } = useCabanaContext();
    const { t } = useTranslation();

    const [showModal, setShowModal] = useState<boolean>(false);

    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [maxTitleLength] = useState<number>(40);
    const [maxSubTitleLength] = useState<number>(20);
    
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<any | null>(null);

    const openDrawer = (product: any) => {
        setSelectedProduct(product);
        setIsDrawerOpen(true);
    };
    //End modal gifter message

    const routeType: string | undefined = props.routeType;

    const [products, setProducts] = useState<any>(props.products);
    const [productToBeDeleted, setProductToBeDeleted] = useState<any>(null);
    const [wishlistProducts] = useState<any>((props.wishlist !== null && props.wishlist.products !== null) ? props.wishlist.products : []);

    const [filtersQueryStr, setFiltersQueryStr] = useState<string>('');
    const [sortQueryStr, setSortQueryStr] = useState<string>('');

    // Update filters and sorting ONLY if they actually change
    useEffect(() => {
        setFiltersQueryStr(props.filtersQueryString ? `&${props.filtersQueryString}` : "");
        setSortQueryStr(props.sortQueryString ? `&sort=${props.sortQueryString}` : "");
    }, [props.filtersQueryString, props.sortQueryString]);

    // Update products ONLY if props.products actually changes
    useEffect(() => {
        setProducts(props.products);
        setIsLoading(false);
    }, [props.products]);

    const handleRemoveItem = (product: any) => {
        setShowModal(true);
        setProductToBeDeleted(product);
    }

    const handleCloseModal = () => {
        setShowModal(false);
    }

    const purchasedDetailsSection = (product: any): JSX.Element | string => {
        if(user.role !== 1) { //message visible only for giftee
            return '';
        }

        if((!product.gifter_id && !product.message_alias && !product.message_body) ||
            (product.message_alias === null && product.message_body === null))
        {
            return '';
        }

        return <div className="PurchasedItemContainer">
            <div className="GifterNameContainer">
                {t('giftedBy')}: {product.message_alias}
            </div>
            <div className="GiterMessageLinkContainer">
                <Link className="d-block d-lg-none" to={`/${userType}/registry/message?wishlistId=${wishlist.list_code}&id=${product.id}`}>
                    <div className="GifterMessageLink">
                        {t('giftedLink')}
                    </div>
                </Link>
                <div
                    className="d-none d-lg-block modalGifterMessageOpener"
                    style={{cursor: 'pointer'}}
                >
                    <div className="GifterMessageLink">
                        {t('giftedLink')}
                    </div>
                </div>
            </div>
        </div>;
    }

    const ProductGrid: React.FC<ProductGridProps> = ({ type, products, wishlistProducts }) => {
        if (products === null || products === undefined || products.data === null) {
            return <div></div>;
        }

        const productsArray = Object.values(products.data);
        return (
            <div className="ProductGrid">
                {productsArray.map((product: any) => {
                    // Find the product in the wishlist that has the same product_code
                    const wishlistProduct = Object.values(wishlistProducts).find(
                        (item: any) => item.product_code === product.product_code
                    ) || {}; // If not found, returns an empty object
                    return (
                        <div className="ProductGridItem" key={product.id}>
                            <ProductPreviewTemplate type={type} product={product} wishlistProduct={wishlistProduct} />
                        </div>
                    );
                })}
            </div>
        );
    };

    const ProductPreviewTemplate: React.FC<{ type: string|undefined, product: any; wishlistProduct: any }> = ({ type, product, wishlistProduct }) => {
        //cart function
        const { cart, addToCart } = useCabanaContext();

        //basically the only thing I care to know is whether the id is present or not
        const cartProductIds = useMemo(() => new Set(cart.map(p => p.id)), [cart]);

        const [quantity, setQuantity] = React.useState("0");
        useEffect(() => {
            if ('wishlist_x_product_quantity_selected' in wishlistProduct) {
                setQuantity(`${wishlistProduct.wishlist_x_product_quantity_selected}`);
            }
        }, [wishlistProduct]);

        const variations = product.variations ?? []; // Returns an empty React element, but the hooks have already been executed

        const price = useMemo(() => {
            if (variations.length === 0) return "0,00";
            let p = parseFloat(variations[0].price_value).toFixed(2);
            return variations[0].price_currency === 'USD' ? `${p}` : p.replace('.', ',');
        }, [variations]);

        const currencySymbol = useMemo(() => {
            const currencySymbol = countriesCurrencies.find((c: any) => c.currency === variations[0].price_currency);
            return currencySymbol !== undefined ? currencySymbol.symbol : variations[0].price_currency;
        }, [variations]);

        let from = variations.length > 1 ? t('from') : '';

        let overImageSection: JSX.Element | string = '';
        let purchasedBadgeElement: JSX.Element | string = '';
        let priceSection: JSX.Element | string = <div className="ProductPreviewPriceContainer">
            <div className="ProductPreviewPrice">
                {from}
                {currencySymbol}
                {price}
            </div>
        </div>;

        let removeSection: any = '';
        let readTheMessageElement: any = '';
        const purchased = useMemo(() => wishlistProduct.wishlist_x_product_quantity_purchased || 0, [wishlistProduct]);
        const imageUrl = useMemo(() => product.image_url || BrokenImage, [product.image_url]);
        if (variations.length === 0) return <></>;
        if(type && type === 'catalogue'){
            if(purchased > 0){
                purchasedBadgeElement = <PurchasedBadge current={wishlistProduct.wishlist_x_product_quantity_purchased} total={wishlistProduct.wishlist_x_product_quantity_selected}/>;
                removeSection = '';
            }else{
                purchasedBadgeElement = '';
                if (user.role !== 3) {
                    if ('added' in wishlistProduct) {
                        overImageSection = <Box className="already-added">{t('productAdded')}</Box>;
                        removeSection =
                            <Box
                                className="CancelItemContainer"
                                onClick={() => {
                                            handleRemoveItem(product)}}
                            >
                                {t('removeProduct')}
                            </Box>;
                    } else {
                        if(!('added' in wishlistProduct)) {
                            if(parseInt(product.catalog_availability) > 0){
                                overImageSection =
                                    <Box
                                        className="to-added"
                                        onClick={() => {
                                            addProductToWishlist(product, quantity)
                                        }}
                                    >ADD
                                    </Box>
                            }else{
                                overImageSection = <div></div>
                            }
                        }else{
                            overImageSection = <Box className="already-added">{t('productAdded')}</Box>
                        }
                    }
                }
            }
        }

        if(type && type === 'wishlist' && userType === 'gifter'){
            removeSection = '';
            overImageSection = <div></div>
            if(purchased > 0){
                purchasedBadgeElement = <PurchasedBadge current={wishlistProduct.wishlist_x_product_quantity_purchased} total={wishlistProduct.wishlist_x_product_quantity_selected}/>;
            }else{
                purchasedBadgeElement = '';
            }
            let canBuyTheProduct = ((parseInt(wishlistProduct.wishlist_x_product_quantity_selected) > parseInt(wishlistProduct.wishlist_x_product_quantity_purchased)) && parseInt(product.catalog_availability) > 0);
            let alreadyAddedToCart = cartProductIds.has(product.id);
            if(alreadyAddedToCart){
                overImageSection = <Box className="already-added">{t('productAdded')}</Box>
            }else{
                if(canBuyTheProduct){
                    overImageSection =
                        <Box
                            className="add-gift"
                            onClick={() => addToCart({...product, list_code: listCode})}
                        >ADD GIFT
                        </Box>
                }else{
                    overImageSection = <div></div>
                }
            }
        }

        if(type && type === 'wishlist' && userType === 'giftee'){
            removeSection = '';
            overImageSection = <div></div>
            if(purchased > 0){
                purchasedBadgeElement = <PurchasedBadge current={wishlistProduct.wishlist_x_product_quantity_purchased} total={wishlistProduct.wishlist_x_product_quantity_selected}/>;
                readTheMessageElement = <Typography className={'read-the-message-label'} onClick={() => openDrawer(wishlistProduct)}>READ THE MESSAGE</Typography>
            }else{
                removeSection =
                    <Box
                        className="CancelItemContainer"
                        onClick={() => {
                            handleRemoveItem(product)}}
                    >
                        {t('removeProduct')}
                    </Box>;
                purchasedBadgeElement = '';
            }
        }

        let categoryId = props.category !== null ? `categoryId=${props.category}` : '';
        let previewClass = routeType === 'catalogue' ? 'ProductPreview' : 'ProductPreview WishlistProductPreview';
        let cabanaMaxQuantity = (process.env.REACT_APP_CABANA_MAX_QUANTITY) ? parseInt(process.env.REACT_APP_CABANA_MAX_QUANTITY) : 20;
        return (
            <div className={previewClass}>
                {purchasedBadgeElement}
                <div className="ProductPreviewImageContainer">
                    {overImageSection}
                    <Link
                        to={`/${userType}/product?id=${product.product_code}&${categoryId}&wishlistId=${wishlist.list_code}&type=${type}${filtersQueryStr}${sortQueryStr}`}>
                        <LazyLoadImage
                            width="460px"
                            height="576px"
                            effect="opacity"
                            wrapperProps={{ style: { transitionDelay: "0.5s" } }}
                            className="ProductPreviewImage img-fluid"
                            src={imageUrl}
                            alt={product.title}
                        />
                    </Link>
                </div>
                <Box sx={{display: "flex", flexDirection: "column", gap: "12px", width: "100%", alignItems: "flex-start", justifyContent: "flex-start", height: "100%"}}>
                    <Box className="ProductPreviewInfo">
                        <Link
                            to={`/${userType}/product?id=${product.product_code}&wishlistId=${wishlist.list_code}${filtersQueryStr}${sortQueryStr}`}>
                            <Box
                                className="ProductPreviewName">{product.title.length > maxTitleLength ? product.title.slice(0, maxTitleLength) + '...' : product.title}
                            </Box>
                        </Link>
                        <Box
                            className="ProductPreviewDescription">
                            {product.subtitle.length > maxSubTitleLength ? product.subtitle.slice(0, maxSubTitleLength) + '...' : product.subtitle}
                        </Box>
                    </Box>
                    <Box className="container-price-and-select-quantity">
                        <div className="ProductOtherInfoContainer">
                            {priceSection}
                        </div>
                        {
                            (parseInt(product.catalog_availability) > 0)
                            ?
                                <QuantityAdjuster
                                    value={parseInt(quantity)}
                                    orderedValue={('wishlist_x_product_quantity_selected' in wishlistProduct) ? wishlistProduct.wishlist_x_product_quantity_selected : 0} maxQuantityOrdered={(parseInt(product.catalog_availability) <= cabanaMaxQuantity) ? parseInt(product.catalog_availability) : cabanaMaxQuantity}
                                    onChange={(newQuantity: number) => setQuantity(`${newQuantity}`)}
                                />
                            :
                                <div></div>
                        }
                    </Box>
                    <Box>
                        {removeSection !== '' ? <Box sx={{cursor: "pointer", fontFamily: 'PT Serif'}}>{removeSection}</Box> : ''}
                        {readTheMessageElement}
                        {purchasedDetailsSection(product) !== '' ? <div>{purchasedDetailsSection(product)}</div> : ''}
                    </Box>
                </Box>
            </div>
        );
    };

    const noProductsTemplate = (): JSX.Element => {
        return (
            <div className="NoProducts">
                <div className="NoProductsText">{t('noProductsFoundCatalogue')}</div>
            </div>
        );
    };

    const productsTemplate = (): JSX.Element => {
        if(isLoading) {
            return <div style={{height:'50vh'}}>
                <Loader />
            </div>
        }

        if (products !== null && products.data !== null && wishlistProducts !== null && products.data.length > 0) {
            return <ProductGrid type={routeType} products={products} wishlistProducts={wishlistProducts}/>;
        } else {
            return noProductsTemplate();
        }
    };

    return (
        <div>
            <Collapse className="FgiftCustomCollapse" in={alertOpen}>
                <Alert
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => {
                                contextSetAlertOpen(false);
                            }}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                    severity={alertType}
                >{alertText}</Alert>
            </Collapse>
            {productsTemplate()}
            <ConfirmDeleteItemModal
                open={showModal}
                onClose={handleCloseModal}
                onConfirm={() => {
                    handleCloseModal();
                    removeProductToWishlist(productToBeDeleted);
                }}
                t={t}
            />
            {
                (routeType && routeType === 'wishlist' && userType === 'giftee') ?
                <MessageDrawer
                    isOpen={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                    modalProduct={selectedProduct}
                    orders={wishlist.orders}
                />
                    : null
            }
        </div>
    );

}

export default withTranslation()(ProductsList);
